import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { HeartPulse, MapPin, Calendar, ExternalLink, AlertCircle, Check, X as XIcon, Clock, Terminal, ChevronDown, ChevronRight } from '../../ui/icons';
import Card from '../../ui/Card';
import Colors, { withAlpha } from '../../../constants/Colors';
import { opsHealthApi, OpsOrchestration, OpsEcsTask, OpsLiveScore } from '../../../services/api';
import GameLogsAccordion from './GameLogsAccordion';

type StatusFilter = 'RUNNING' | 'FAILED' | 'SUCCEEDED' | 'ABORTED';

const STATUS_FILTERS: StatusFilter[] = ['RUNNING', 'FAILED', 'SUCCEEDED', 'ABORTED'];
const POLL_INTERVAL_MS = 10000;

// RUNNING has no matching .status-* class in this theme (the shared .status-info class is
// styled for a dark background and would render near-invisible here), so its badge is
// built from an inline style instead of a class, unlike the other three statuses.
const STATUS_TONE: Record<StatusFilter, { border: string; badge: 'status-success' | 'status-warning' | 'status-error' | null }> = {
  RUNNING: { border: Colors.secondary, badge: null },
  FAILED: { border: Colors.error, badge: 'status-error' },
  SUCCEEDED: { border: Colors.success, badge: 'status-success' },
  ABORTED: { border: '#FF9500', badge: 'status-warning' },
};

const PHASE_LABELS: Record<string, string> = {
  'pre-game': 'Pre-Game',
  'mqtt-bot': 'MQTT',
  'audio-setup': 'Audio',
  tipoff: 'Tipoff',
  live: 'Live',
  'post-game': 'Post-Game',
};

function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return '—';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.round((seconds % 3600) / 60);
  return `${hours}h ${minutes}m`;
}

function cleanFamily(family: string): string {
  return family.replace(/^(dev|prod|qa)-/, '');
}

function taskLabel(task: OpsEcsTask): string {
  const family = cleanFamily(task.family).toLowerCase();
  if (family.includes('audio')) {
    return task.stream_direction ? `Audio - ${task.stream_direction === 'home' ? 'Home' : 'Away'}` : 'Audio';
  }
  if (family.includes('tipoff')) return 'Radio Tipoff';
  if (family.includes('mqtt')) return 'MQTT Bot';
  return cleanFamily(task.family);
}

export default function ServiceHealthPanel() {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('RUNNING');
  const [orchestrations, setOrchestrations] = useState<OpsOrchestration[]>([]);
  const [ecsTasks, setEcsTasks] = useState<OpsEcsTask[]>([]);
  const [scoresByGameName, setScoresByGameName] = useState<Record<string, OpsLiveScore | null>>({});
  const [loading, setLoading] = useState(false);
  const [orchestrationsError, setOrchestrationsError] = useState<string | null>(null);
  const [ecsTasksError, setEcsTasksError] = useState<string | null>(null);
  const [expandedLogs, setExpandedLogs] = useState<Record<string, boolean>>({});

  const toggleLogs = (key: string) => {
    setExpandedLogs((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const fetchAll = useCallback(async (showSpinner: boolean) => {
    if (showSpinner) setLoading(true);
    const [orchResponse, tasksResponse] = await Promise.all([
      opsHealthApi.getOrchestrations(20),
      opsHealthApi.getEcsTasks(),
    ]);

    let liveOrchestrations: OpsOrchestration[] = [];
    if (orchResponse.success && orchResponse.data?.success) {
      setOrchestrationsError(null);
      liveOrchestrations = orchResponse.data.orchestrations || [];
      setOrchestrations(liveOrchestrations);
    } else {
      setOrchestrationsError(orchResponse.error || orchResponse.data?.error || 'Failed to load orchestrations');
    }

    if (tasksResponse.success && tasksResponse.data?.success) {
      setEcsTasksError(null);
      setEcsTasks(tasksResponse.data.tasks || []);
    } else {
      setEcsTasksError(tasksResponse.error || tasksResponse.data?.error || 'Failed to load ECS tasks');
    }

    // Live score only means anything for currently-running games. The genius/MQTT data table
    // this endpoint reads from keys its records by game_name, not the games-table game_id, so
    // game_name is what gets sent through the `game_id` query param.
    const runningGameNames = Array.from(new Set(
      liveOrchestrations.filter((o) => o.status === 'RUNNING' && o.game_name).map((o) => o.game_name)
    ));
    if (runningGameNames.length > 0) {
      const gameTypeByName = new Map(liveOrchestrations.map((o) => [o.game_name, o.game_type || 'football']));
      const scoreResponses = await Promise.all(
        runningGameNames.map((gameName) => opsHealthApi.getLiveScore(gameName, gameTypeByName.get(gameName) || 'football'))
      );
      setScoresByGameName((prev) => {
        const next = { ...prev };
        runningGameNames.forEach((gameName, i) => {
          const res = scoreResponses[i];
          next[gameName] = res.success && res.data?.success ? res.data.score : prev[gameName] ?? null;
        });
        return next;
      });
    }

    if (showSpinner) setLoading(false);
  }, []);

  useEffect(() => {
    fetchAll(true);
  }, [fetchAll]);

  useEffect(() => {
    const timer = setInterval(() => fetchAll(false), POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [fetchAll]);

  const { games } = useMemo(() => {
    // Latest orchestration per game, then filtered by the active status tab.
    const latestByGame = new Map<string, OpsOrchestration>();
    orchestrations.forEach((o) => {
      const key = o.game_name || o.game_id || o.execution_arn;
      const existing = latestByGame.get(key);
      if (!existing || o.started_at > existing.started_at) latestByGame.set(key, o);
    });

    const tasksByGameMap = new Map<string, OpsEcsTask[]>();
    ecsTasks.forEach((t) => {
      const key = t.game_name || 'unknown';
      if (!tasksByGameMap.has(key)) tasksByGameMap.set(key, []);
      tasksByGameMap.get(key)!.push(t);
    });

    const gameKeys = new Set<string>();
    latestByGame.forEach((o, key) => {
      if (o.status === statusFilter) gameKeys.add(key);
    });
    if (statusFilter === 'RUNNING') {
      tasksByGameMap.forEach((_tasks, key) => gameKeys.add(key));
    }

    const gameList = Array.from(gameKeys).map((key) => ({
      key,
      orchestration: latestByGame.get(key) || null,
      tasks: tasksByGameMap.get(key) || [],
    }));
    gameList.sort((a, b) => (b.orchestration?.started_at || '').localeCompare(a.orchestration?.started_at || ''));

    return { games: gameList };
  }, [orchestrations, ecsTasks, statusFilter]);

  return (
    <div className="section">
      <h2 className="section-title">
        <HeartPulse size={24} />
        Service Health
      </h2>

      <Card
        title="Live game orchestration"
        description="Step Function executions and Bot/audio/tipoff ECS task status"
        headerAction={
          <div style={{ display: 'flex', gap: '3px', background: Colors.background, borderRadius: '999px', padding: '3px' }}>
            {STATUS_FILTERS.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setStatusFilter(s)}
                style={{
                  height: '30px', padding: '0 14px', borderRadius: '999px', border: 0, cursor: 'pointer',
                  fontSize: '12px', fontWeight: 700,
                  background: statusFilter === s ? '#FFFFFF' : 'transparent',
                  color: statusFilter === s ? Colors.text : Colors.textLight,
                  boxShadow: statusFilter === s ? '0 1px 3px rgba(21, 41, 92, 0.16)' : 'none',
                }}
              >
                {s}
              </button>
            ))}
          </div>
        }
      >
        {orchestrationsError && (
          <div className="alert alert-warning" style={{ marginBottom: '16px' }}>
            <AlertCircle size={20} />
            <div>{orchestrationsError}</div>
          </div>
        )}
        {ecsTasksError && (
          <div className="alert alert-warning" style={{ marginBottom: '16px' }}>
            <AlertCircle size={20} />
            <div>{ecsTasksError}</div>
          </div>
        )}

        {games.length === 0 && !loading && (
          <div style={{ textAlign: 'center', padding: '40px', color: Colors.textLight }}>
            <HeartPulse size={28} style={{ opacity: 0.4, marginBottom: '8px' }} />
            <div>No {statusFilter.toLowerCase()} games right now</div>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {games.map(({ key, orchestration, tasks }) => {
            const tone = STATUS_TONE[orchestration?.status as StatusFilter] || STATUS_TONE.RUNNING;
            const score = orchestration?.game_name ? scoresByGameName[orchestration.game_name] : null;
            return (
              <div
                key={key}
                className="card"
                style={{ boxShadow: 'none', border: '1px solid rgba(var(--color-border-rgb), 0.18)', borderLeft: `4px solid ${tone.border}`, display: 'flex', flexDirection: 'column', gap: '14px' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: '16px', fontWeight: 700 }}>{orchestration?.game_name || key}</div>
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', fontSize: '12px', color: Colors.textLight, marginTop: '3px' }}>
                      {orchestration?.game_type && <span>{orchestration.game_type}</span>}
                      {orchestration?.venue && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><MapPin size={12} />{orchestration.venue}</span>
                      )}
                      {orchestration?.started_at && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Calendar size={12} />Started {new Date(orchestration.started_at).toLocaleString()}
                        </span>
                      )}
                    </div>
                  </div>
                  {score && (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '6px 16px', borderRadius: '10px', background: Colors.background }}>
                      <span style={{ fontSize: '15px', fontWeight: 800, color: Colors.text, letterSpacing: '0.5px' }}>
                        {score.home_score} – {score.guest_score}
                      </span>
                      {(score.period_text || score.clock) && (
                        <span style={{ fontSize: '10.5px', color: Colors.textLight, fontWeight: 600, whiteSpace: 'nowrap' }}>
                          {[score.period_text, score.clock].filter(Boolean).join(' · ')}
                        </span>
                      )}
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto' }}>
                    <button
                      type="button"
                      onClick={() => toggleLogs(key)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '5px', height: '28px', padding: '0 10px', borderRadius: '999px',
                        border: '1px solid rgba(var(--color-border-rgb), 0.2)', background: '#FFFFFF', color: Colors.text,
                        fontSize: '11.5px', fontWeight: 600, cursor: 'pointer',
                      }}
                    >
                      <Terminal size={12} />
                      Logs
                      {expandedLogs[key] ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                    </button>
                    {orchestration && (
                      <>
                        {orchestration.current_phase && orchestration.current_phase !== 'unknown' && (
                          <span style={{ fontSize: '11.5px', fontWeight: 700, color: Colors.textLight }}>
                            {PHASE_LABELS[orchestration.current_phase] || orchestration.current_phase}
                          </span>
                        )}
                        {tone.badge ? (
                          <span className={`status-badge ${tone.badge}`}>{orchestration.status}</span>
                        ) : (
                          <span
                            className="status-badge"
                            style={{ background: withAlpha(Colors.secondary, 0.16), color: Colors.secondary, borderColor: Colors.secondary }}
                          >
                            {orchestration.status}
                          </span>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {orchestration && orchestration.phase_timeline.length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0' }}>
                    {orchestration.phase_timeline.map((phase, i) => {
                      const phaseTone = phase.status === 'completed'
                        ? { bg: withAlpha(Colors.success, 0.16), fg: Colors.success, icon: Colors.success }
                        : phase.status === 'in_progress'
                          ? { bg: 'rgba(255, 149, 0, 0.16)', fg: '#FF9500', icon: '#FF9500' }
                          : phase.status === 'failed'
                            ? { bg: withAlpha(Colors.error, 0.16), fg: Colors.error, icon: Colors.error }
                            : { bg: 'rgba(var(--color-border-rgb), 0.12)', fg: Colors.textLight, icon: Colors.inactive };
                      const Icon = phase.status === 'completed' ? Check : phase.status === 'failed' ? XIcon : Clock;
                      return (
                        <div key={phase.phase} style={{ display: 'flex', alignItems: 'center' }}>
                          <div style={{
                            display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '5px 11px', borderRadius: '999px',
                            fontSize: '11.5px', fontWeight: 600, background: phaseTone.bg, color: phaseTone.fg,
                          }}
                          >
                            <span style={{ width: '15px', height: '15px', borderRadius: '999px', display: 'grid', placeItems: 'center', background: phaseTone.icon, color: '#fff', flex: 'none' }}>
                              <Icon size={9} />
                            </span>
                            <span style={{ whiteSpace: 'nowrap' }}>{PHASE_LABELS[phase.phase] || phase.phase}</span>
                          </div>
                          {i < orchestration.phase_timeline.length - 1 && (
                            <span style={{ width: '14px', height: '2px', flex: 'none', background: phase.status === 'completed' ? withAlpha(Colors.success, 0.4) : 'rgba(var(--color-border-rgb), 0.24)' }} />
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {tasks.length > 0 && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px' }}>
                    {tasks.map((t) => {
                      const isRunning = t.status === 'RUNNING';
                      return (
                        <div
                          key={t.task_arn}
                          style={{
                            display: 'flex', flexDirection: 'column', gap: '5px', padding: '11px 13px', borderRadius: '10px',
                            background: '#FFFFFF', border: `1px solid ${isRunning ? 'rgba(var(--color-border-rgb), 0.16)' : withAlpha(Colors.error, 0.28)}`,
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                            <span style={{ fontSize: '12.5px', fontWeight: 700 }}>{taskLabel(t)}</span>
                            <span
                              style={{
                                width: '8px', height: '8px', borderRadius: '999px',
                                background: isRunning ? Colors.success : Colors.error,
                                animation: isRunning ? 'pulse 1.8s ease-in-out infinite' : 'none',
                              }}
                            />
                          </div>
                          <div style={{ fontSize: '12px', color: Colors.text }}>
                            {isRunning ? `Up ${formatDuration(t.uptime_seconds)}` : (t.stopped_reason || t.status)}
                          </div>
                          {t.stream_url && (
                            <a
                              href={t.stream_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11.5px', color: Colors.secondary, textDecoration: 'none' }}
                            >
                              <ExternalLink size={11} />
                              Stream
                            </a>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {expandedLogs[key] && (
                  <GameLogsAccordion
                    gameName={orchestration?.game_name || key}
                    gameType={orchestration?.game_type || 'football'}
                  />
                )}
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
